import { GALLERY_PAGE_SIZE, getNextOffset } from '@/lib/offset-pagination';
import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { listAssetsResponseSchema } from '@borradh-workspace/contracts';
import {
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import type { AssetSource, AssetType, ListAssetsResponse } from '../types';

interface ListAssetsParams {
  type?: AssetType;
  source?: AssetSource;
  tags?: string[];
  limit?: number;
  offset?: number;
}

/**
 * Query options for listing assets
 */
export const listAssetsQueryOptions = (params?: ListAssetsParams) => {
  const queryParams = new URLSearchParams();
  if (params?.type) queryParams.set('type', params.type);
  if (params?.source) queryParams.set('source', params.source);
  if (params?.tags && params.tags.length > 0)
    queryParams.set('tags', params.tags.join(','));
  if (params?.limit) queryParams.set('limit', params.limit.toString());
  if (params?.offset) queryParams.set('offset', params.offset.toString());
  const queryString = queryParams.toString();

  return queryOptions({
    queryKey: [
      'assets',
      params?.type,
      params?.source,
      params?.tags,
      params?.limit,
      params?.offset,
    ],
    queryFn: async () => {
      return apiClient.get<ListAssetsResponse>(
        `assets${queryString ? `?${queryString}` : ''}`,
        { schema: listAssetsResponseSchema }
      );
    },
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * List Assets Hook
 * Returns all assets for the current organization
 */
export const useListAssets = (params?: ListAssetsParams) => {
  const query = useQuery(listAssetsQueryOptions(params));

  return {
    assets: query.data?.items ?? [],
    /** Server-side total for the query — may exceed `assets.length` when the
     *  `limit` truncates the result, so callers can surface "showing N of M". */
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Offset-paged assets for the gallery's "Load more" button. Pages are fetched
 * newest-first until the server total is reached.
 */
export const useInfiniteListAssets = (params: { type?: AssetType }) => {
  const query = useInfiniteQuery({
    queryKey: queryKeys.assets.infinite(params.type),
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const queryParams = new URLSearchParams({
        limit: String(GALLERY_PAGE_SIZE),
        offset: String(pageParam),
      });
      if (params.type) queryParams.set('type', params.type);
      return apiClient.get<ListAssetsResponse>(`assets?${queryParams}`, {
        schema: listAssetsResponseSchema,
      });
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextOffset(lastPage, allPages, GALLERY_PAGE_SIZE),
    staleTime: 30 * 1000,
  });

  return {
    assets: query.data?.pages.flatMap((page) => page.items) ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
